"use client";

import { getPalette, type PaletteToken } from "@/lib/palette";
import { usePrefersDark } from "@/lib/usePrefersDark";
import { WaffleGrid, type WaffleItem } from "@/components/charts/WaffleGrid";

export const ACTIONS_STATUS = {
  underway: "Currently underway",
  notYet: "Not yet underway",
} as const;

export type ActionsRow = {
  status: string;
  action_count: number;
};

type WaffleStyle = { token: PaletteToken; shape: "filled" | "open" };

const STATE_TOKEN: Record<string, WaffleStyle> = {
  [ACTIONS_STATUS.underway]: { token: "teal", shape: "filled" },
  [ACTIONS_STATUS.notYet]: { token: "slate", shape: "open" },
};

const FALLBACK_STYLE: WaffleStyle = { token: "slate", shape: "filled" };

export function ClimateActionsWaffle({ data }: { data: ActionsRow[] }) {
  const p = getPalette(usePrefersDark());
  const styleFor = (status: string) => {
    const t = STATE_TOKEN[status] ?? FALLBACK_STYLE;
    return { color: p[t.token], shape: t.shape };
  };
  const items: WaffleItem[] = data.flatMap((row) => {
    const style = styleFor(row.status);
    return Array.from({ length: row.action_count }, (_, i) => ({
      key: `${row.status}-${i}`,
      status: row.status,
      color: style.color,
      shape: style.shape,
    }));
  });

  const total = items.length;
  const underway =
    data.find((r) => r.status === ACTIONS_STATUS.underway)?.action_count ?? 0;

  return (
    <div className="overflow-x-auto">
      <WaffleGrid
        items={items}
        cols={20}
        size={16}
        gap={6}
        ariaLabel={`${underway} of ${total} climate actions currently underway`}
        legend={data.map((row) => {
          const style = styleFor(row.status);
          return {
            label: `${row.status} (${row.action_count})`,
            color: style.color,
            shape: style.shape,
          };
        })}
      />
    </div>
  );
}
